import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import MeetingSuggestions from '../components/MeetingSuggestions';
import TaskKanban from '../components/TaskKanban';
import TaskComments from '../components/TaskComments';
import TaskList, { CATEGORY_OPTIONS } from '../components/TaskList';
import TaskPanel from '../components/TaskPanel';
import BrandSidebar from '../components/BrandSidebar';
import { useSubTab, useSubnavCounts } from '../components/Layout';
import MeetingVideoModal, { fmtTs } from '../components/MeetingVideoModal';
import Icon from '../components/Icon';

const PRIORITY_MAP = {
  high: { label: 'Yüksek', color: '#FF6B5B' },
  medium: { label: 'Orta', color: '#F2B84B' },
  low: { label: 'Düşük', color: '#34D399' },
};

const STATUS_MAP = {
  todo: { label: 'Yapılacak', color: '#9A9A92' },
  in_progress: { label: 'Devam Ediyor', color: '#D4FF3A' },
  on_client: { label: 'Müşteride bekliyor', color: '#FF8A3D' },
  on_hold: { label: 'Beklemede', color: '#A78BFA' },
  done: { label: 'Tamamlandı', color: '#34D399' },
};
const STATUS_OPTIONS = Object.entries(STATUS_MAP);

const EMPTY_FORM = {
  title: '', description: '', assignee_ids: [], related_to: 'internal',
  due_date: '', priority: 'medium', status: 'todo',
  company: '', last_action: '', next_step: '', approval_required: false, account_id: '', category: ''
};

export default function Tasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [quick, setQuick] = useState('all'); // all | mine | week | client
  // Varsayılan görünüm: ClickUp tarzı gruplu liste (eski Tablo ve Kanban yerinde duruyor)
  const [view, setView] = useState(() => { try { return localStorage.getItem('tasksView2') || 'list'; } catch (e) { return 'list'; } });
  // Varsayılan düzen ClickUp klasörleri gibi: Marka → (alt grup) Durum
  const [groupBy, setGroupBy] = useState(() => { try { return localStorage.getItem('tasksGroupBy2') || 'account'; } catch (e) { return 'account'; } });
  // Marka içinde kategori (ClickUp'taki klasör → liste); tamamlananlar varsayılan gizli
  const [subGroupBy, setSubGroupBy] = useState(() => { try { return localStorage.getItem('tasksSubGroup3') || 'category'; } catch (e) { return 'category'; } });
  const [showDone, setShowDone] = useState(() => { try { return localStorage.getItem('tasksShowDone') === '1'; } catch (e) { return false; } });
  // Soldaki marka paneli: null = tüm markalar, 'none' = markasız, sayı = hesap id
  const [brandFilter, setBrandFilter] = useState(null);
  const [panelId, setPanelId] = useState(null);
  // Bugünüm'den gelen bağlantı: /tasks?open=ID → görev paneli açılır
  const [searchParams, setSearchParams] = useSearchParams();
  const openParam = Number(searchParams.get('open')) || null;
  useEffect(() => {
    if (!openParam || !tasks.some(t => t.id === openParam)) return;
    setPanelId(openParam);
    const next = new URLSearchParams(searchParams); next.delete('open'); setSearchParams(next, { replace: true });
  }, [openParam, tasks]); // eslint-disable-line react-hooks/exhaustive-deps
  // Ana grup ile alt grup aynı olamaz: ana grup değişince uygun alt gruba geç
  useEffect(() => {
    if (subGroupBy === groupBy) setSubGroupBy(groupBy === 'account' ? 'category' : 'account');
  }, [groupBy, subGroupBy]);
  const [accounts, setAccounts] = useState([]);
  const [dateField, setDateField] = useState('updated_at');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const tab = useSubTab('/tasks'); // üst menüde Görevler'in altındaki sekme
  const [suggestionCount, setSuggestionCount] = useState(null);
  const [suggestionRefresh, setSuggestionRefresh] = useState(0);
  const [fromItem, setFromItem] = useState(null);
  const [player, setPlayer] = useState(null);
  useSubnavCounts(suggestionCount != null ? { suggestions: suggestionCount } : {});

  useEffect(() => { try { localStorage.setItem('tasksView2', view); localStorage.setItem('tasksGroupBy2', groupBy); localStorage.setItem('tasksSubGroup3', subGroupBy); localStorage.setItem('tasksShowDone', showDone ? '1' : '0'); } catch (e) { /* tarayıcı izin vermiyorsa önemsiz */ } }, [view, groupBy, subGroupBy, showDone]);

  useEffect(() => {
    fetchTasks();
    fetchUsers();
    api.get('/accounts').then(r => setAccounts(r.data.filter(a => a.status !== 'churned')
      .map(a => ({ id: a.id, name: a.brand_name || a.company_name, am_id: a.am_id, ppc_id: a.ppc_id })).sort((a, b) => a.name.localeCompare(b.name, 'tr')))).catch(() => {});
    api.get('/meetings/action-items').then(r => setSuggestionCount(r.data.length)).catch(() => {});
  }, []);

  const openPlayer = async (meetingId, startAt) => {
    try {
      const r = await api.get(`/meetings/${meetingId}`);
      setPlayer({ meeting: r.data, startAt });
    } catch (err) {
      alert(err.response?.data?.error || 'Toplantı açılamadı');
    }
  };

  // "Toplantıdan Gelenler" → mevcut görev formu, öneriden doldurulmuş
  const openFromItem = (item) => {
    const when = item.meeting_date ? new Date(item.meeting_date).toLocaleDateString('tr-TR') : '';
    setEditingId(null);
    setFromItem(item);
    setForm({
      ...EMPTY_FORM,
      title: String(item.text || '').slice(0, 200),
      company: item.brand_name || '',
      related_to: item.account_id ? 'account' : 'internal',
      related_id: item.account_id || null,
      account_id: item.account_id || '',
      assignee_ids: item.assignee_user_id ? [item.assignee_user_id] : [],
      description: [
        `Toplantı: ${item.meeting_title}${when ? ` (${when})` : ''}`,
        item.ts_seconds != null ? `Kayıttaki an: ${fmtTs(item.ts_seconds)}` : null,
        item.assignee_name ? `Toplantıda atanan: ${item.assignee_name}` : null,
      ].filter(Boolean).join('\n'),
      meeting_item_id: item.id,
    });
    setShowForm(true);
  };

  const fetchTasks = async () => {
    try {
      const res = await api.get('/tasks');
      setTasks(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const openNew = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  // Var olan görev sağdaki detay panelinde açılır (eski form penceresi yeni görev için kullanılıyor)
  const openTask = (task) => setPanelId(task.id);

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFromItem(null);
    setForm(EMPTY_FORM);
    setAssigneeOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, assignee_ids: (form.assignee_ids || []).map(Number), account_id: form.account_id ? Number(form.account_id) : null, category: form.category || null };
      const wasFromItem = !!payload.meeting_item_id;
      if (editingId) {
        await api.put(`/tasks/${editingId}`, payload);
      } else {
        await api.post('/tasks', payload);
      }
      closeForm();
      fetchTasks();
      if (wasFromItem) setSuggestionRefresh(n => n + 1);
    } catch (err) {
      console.error(err);
      if (form.meeting_item_id) {
        alert(err.response?.data?.error || 'Görev kaydedilemedi');
        if (err.response?.status === 409) { closeForm(); setSuggestionRefresh(n => n + 1); }
      }
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    if (!window.confirm('Bu görev silinecek. Emin misiniz?')) return;
    try {
      await api.delete(`/tasks/${editingId}`);
      closeForm();
      fetchTasks();
    } catch (err) {
      console.error(err);
    }
  };

  const editingTask = editingId ? tasks.find(t => t.id === editingId) : null;

  // Satır içi / panel düzenleme: ekranda hemen değişir, sunucunun döndürdüğü satırla kesinleşir
  const patchLocal = (id, patch) => setTasks(ts => ts.map(t => (t.id === id ? { ...t, ...patch } : t)));
  const updateTask = async (id, patch, opts = {}) => {
    // Toplantıdan gelmiş göreve marka atanıyorsa: aynı toplantının diğer markasız görevleri de o markaya geçsin mi?
    const before = tasks.find(t => t.id === id);
    const mid = before && before.source_meeting && before.source_meeting.meeting_id;
    if (patch.account_id && mid && !opts.skipMeetingAsk) {
      const siblings = tasks.filter(t => t.id !== id && !t.account_id && t.source_meeting && t.source_meeting.meeting_id === mid);
      const brand = accounts.find(a => a.id === patch.account_id)?.name || 'bu marka';
      if (siblings.length && window.confirm(
        `Bu görev "${before.source_meeting.title || 'toplantı'}" toplantısından geldi. Aynı toplantıdan ${siblings.length} markasız görev daha var.\n\n` +
        `Hepsi "${brand}" markasına bağlansın mı? Toplantıda konuşan müşteriler de bu markaya öğretilir; sonraki toplantılarında marka otomatik bulunur.`)) {
        try { await api.post(`/meetings/${mid}/brand`, { account_id: patch.account_id }); } catch (e) { /* toast api katmanında */ }
        return fetchTasks();
      }
    }
    const optimistic = { ...patch };
    if (patch.assignee_ids) optimistic.assignees = users.filter(u => patch.assignee_ids.includes(u.id)).map(u => ({ id: u.id, name: u.name }));
    if ('account_id' in patch) optimistic.account_name = accounts.find(a => a.id === patch.account_id)?.name || null;
    patchLocal(id, optimistic);
    try {
      const r = await api.put(`/tasks/${id}`, patch);
      if (r.data && r.data.id) setTasks(ts => ts.map(t => (t.id === id ? { ...t, ...r.data } : t)));
    } catch (err) {
      fetchTasks();
    }
  };
  // Markasız görevleri toplu olarak bir markaya bağla
  const bulkBrand = async (ids, accountId) => {
    for (const id of ids) await updateTask(id, { account_id: accountId }, { skipMeetingAsk: true });
  };
  const quickCreate = async (payload) => {
    // Sol panelde bir marka seçiliyse hızlı eklenen görev o markaya bağlanır
    if (typeof brandFilter === 'number' && !payload.account_id) payload = { ...payload, account_id: brandFilter };
    const r = await api.post('/tasks', payload);
    setTasks(ts => [...ts, r.data]);
  };
  const deleteFromPanel = async (id) => {
    if (!window.confirm('Bu görev silinecek. Emin misiniz?')) return;
    try {
      await api.delete(`/tasks/${id}`);
      setPanelId(null);
      setTasks(ts => ts.filter(t => t.id !== id));
    } catch (err) { /* hata api katmanında gösterilir */ }
  };
  const panelTask = panelId ? tasks.find(t => t.id === panelId) : null;

  const updateTaskStatus = async (id, status) => {
    try {
      await api.put(`/tasks/${id}`, { status });
      fetchTasks();
    } catch (err) {
      console.error(err);
    }
  };

  const matchDateRange = (t) => {
    if (!dateFrom && !dateTo) return true;
    const v = t[dateField];
    if (!v) return false;
    const d = String(v).slice(0, 10); // YYYY-MM-DD
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  };

  // Hızlı filtreler: Bana ait (atanan veya açtığım) · Bu hafta (7 gün içinde biten veya gecikmiş) · Müşteride bekleyen
  const isMine = (t) => t.created_by === user?.id || t.assigned_to === user?.id
    || (Array.isArray(t.assignees) && t.assignees.some(a => a.id === user?.id));
  const inWeek = (t) => {
    if (t.status === 'done' || !t.due_date) return false;
    const d = new Date(t.due_date); const lim = new Date(); lim.setHours(0, 0, 0, 0); lim.setDate(lim.getDate() + 7);
    return d < lim;
  };
  const matchQuick = (t) => quick === 'mine' ? isMine(t) : quick === 'week' ? inWeek(t) : quick === 'client' ? t.status === 'on_client' : true;
  const quickCounts = { mine: tasks.filter(isMine).length, week: tasks.filter(inWeek).length, client: tasks.filter(t => t.status === 'on_client').length };

  const filtered = tasks.filter(t => {
    const q = search.toLowerCase();
    const matchSearch = t.title?.toLowerCase().includes(q) ||
      t.company?.toLowerCase().includes(q) ||
      t.account_name?.toLowerCase().includes(q) ||
      t.last_action?.toLowerCase().includes(q) ||
      t.assigned_name?.toLowerCase().includes(q) ||
      (Array.isArray(t.assignees) && t.assignees.some(a => a.name?.toLowerCase().includes(q)));
    const matchStatus = !filterStatus || t.status === filterStatus;
    const matchBrand = brandFilter == null || (brandFilter === 'none' ? !t.account_id : t.account_id === brandFilter);
    // Liste görünümünde tamamlananlar, kullanıcı açmadıkça gizli (durum filtresiyle seçilirse görünür)
    const matchDone = view !== 'list' || showDone || filterStatus === 'done' || t.status !== 'done';
    return matchSearch && matchStatus && matchDateRange(t) && matchQuick(t) && matchBrand && matchDone;
  });

  const formatUpdated = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('tr-TR');
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    const diff = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    if (diff < 0) return <span style={{ color: '#FF6B5B' }}>Gecikmiş ({Math.abs(diff)} gün)</span>;
    if (diff === 0) return <span style={{ color: '#FF6B5B' }}>Bugün</span>;
    if (diff === 1) return <span style={{ color: '#F2B84B' }}>Yarın</span>;
    return new Date(d).toLocaleDateString('tr-TR');
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{tab === 'meeting' ? 'Toplantıdan gelenler' : 'Görevler'}</h1>
          <p className="page-subtitle">{tab === 'meeting' ? 'Fireflies toplantılarındaki yapılacaklar — göreve çevir veya yoksay' : 'Ekip görevleri ve takip'}</p>
        </div>
        <div className="page-actions">
          {tab === 'tasks' && (<>
          <div className="search-bar">
            <span className="search-bar__icon"><Icon name="search" size={15} /></span>
            <input className="search-bar__input" placeholder="Görev ara..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <select className="form-input" style={{ width: 'auto' }} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">Tüm Durumlar</option>
            {STATUS_OPTIONS.map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select className="form-input" style={{ width: 'auto' }} value={dateField} onChange={(e) => setDateField(e.target.value)} title="Tarih filtresi hangi alana uygulansın">
            <option value="updated_at">Son Güncelleme</option>
            <option value="due_date">Bitiş</option>
            <option value="created_at">Oluşturulma</option>
          </select>
          <input className="form-input" style={{ width: 'auto' }} type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} title="Başlangıç" />
          <span className="text-muted" style={{ alignSelf: 'center' }}>–</span>
          <input className="form-input" style={{ width: 'auto' }} type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} title="Bitiş" />
          {(dateFrom || dateTo) && (
            <button className="btn btn--secondary" onClick={() => { setDateFrom(''); setDateTo(''); }} title="Tarih filtresini temizle">✕</button>
          )}
          </>)}
          <button className="btn btn--primary" onClick={openNew}>+ Yeni Görev</button>
        </div>
      </div>

      {tab === 'meeting' && (
        <MeetingSuggestions onAccept={openFromItem} onPlay={openPlayer}
          onCountChange={setSuggestionCount} refreshKey={suggestionRefresh} />
      )}

      {tab === 'tasks' && (
        <div className="qf">
          {[['all', 'Tümü', tasks.length], ['mine', 'Bana ait', quickCounts.mine], ['week', 'Bu hafta', quickCounts.week], ['client', 'Müşteride bekleyen', quickCounts.client]].map(([k, label, n]) => (
            <button key={k} type="button" className={`btn ${quick === k ? 'btn--selected' : 'btn--secondary'}`} style={{ padding: '4px 12px', fontSize: 13 }} onClick={() => setQuick(k)}>
              {label} <span style={{ opacity: 0.7, marginLeft: 4 }}>{n}</span>
            </button>
          ))}
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
            {view === 'list' && (
              <select className="form-input" style={{ width: 'auto', padding: '4px 10px', fontSize: 13 }} value={groupBy} onChange={(e) => setGroupBy(e.target.value)} title="Listeyi neye göre grupla">
                <option value="status">Grupla: Durum</option>
                <option value="account">Grupla: Marka</option>
                <option value="assignee">Grupla: Kişi</option>
                <option value="category">Grupla: Kategori</option>
              </select>
            )}
            {view === 'list' && (
              <select className="form-input" style={{ width: 'auto', padding: '4px 10px', fontSize: 13 }} value={subGroupBy} onChange={(e) => setSubGroupBy(e.target.value)} title="Her grubun içinde alt gruplama">
                <option value="none">Alt grup: Yok</option>
                {groupBy !== 'account' && <option value="account">Alt grup: Marka</option>}
                {groupBy !== 'assignee' && <option value="assignee">Alt grup: Kişi</option>}
                {groupBy !== 'status' && <option value="status">Alt grup: Durum</option>}
                {groupBy !== 'category' && <option value="category">Alt grup: Kategori</option>}
              </select>
            )}
            {view === 'list' && (
              <label className="text-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', margin: '0 6px' }}>
                <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} /> Tamamlananlar
              </label>
            )}
            <button type="button" className={`btn ${view === 'list' ? 'btn--selected' : 'btn--secondary'}`} style={{ padding: '4px 12px', fontSize: 13 }} onClick={() => setView('list')}>Liste</button>
            <button type="button" className={`btn ${view === 'table' ? 'btn--selected' : 'btn--secondary'}`} style={{ padding: '4px 12px', fontSize: 13 }} onClick={() => setView('table')}>Tablo</button>
            <button type="button" className={`btn ${view === 'kanban' ? 'btn--selected' : 'btn--secondary'}`} style={{ padding: '4px 12px', fontSize: 13 }} onClick={() => setView('kanban')}>Kanban</button>
          </span>
        </div>
      )}

      {tab === 'tasks' && (
        <div className="tasks-layout">
          <BrandSidebar accounts={accounts} tasks={tasks.filter(matchQuick)} userId={user?.id} value={brandFilter} onChange={setBrandFilter} />
          <div className="tasks-main">
      {view === 'kanban' && !loading && (
        <TaskKanban tasks={filtered} statuses={STATUS_OPTIONS} onMove={updateTaskStatus} onOpen={openTask} formatDue={formatDate} />
      )}

      {view === 'list' && (loading
        ? <div className="page-loading">Yükleniyor...</div>
        : <TaskList tasks={filtered} users={users} accounts={accounts} onBulkBrand={bulkBrand} groupBy={groupBy} subGroupBy={subGroupBy === 'none' ? null : subGroupBy} statuses={STATUS_OPTIONS} statusMap={STATUS_MAP} priorityMap={PRIORITY_MAP}
            onUpdate={updateTask} onCreate={quickCreate} onOpen={openTask} onPlay={openPlayer} selectedId={panelId} />
      )}

      {panelTask && (
        <TaskPanel task={panelTask} users={users} accounts={accounts} statuses={STATUS_OPTIONS} statusMap={STATUS_MAP} priorityMap={PRIORITY_MAP}
          canDelete={user?.role === 'admin' || panelTask.created_by === user?.id}
          onUpdate={updateTask} onDelete={deleteFromPanel} onClose={() => setPanelId(null)} onPlay={openPlayer} onLocalChange={patchLocal} />
      )}

      {view === 'table' && (
      <div className="card" style={{ overflow: 'hidden' }}>
        {loading ? (
          <div className="page-loading">Yükleniyor...</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Marka</th>
                <th>Görev</th>
                <th>Yapılan Son İşlem</th>
                <th>Atanan</th>
                <th>Durum</th>
                <th>Onay</th>
                <th>Son Güncelleme</th>
                <th>Öncelik</th>
                <th>İlgili</th>
                <th>Bitiş</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(task => (
                <tr key={task.id} onClick={() => openTask(task)} style={{ cursor: 'pointer' }}>
                  <td style={{ fontWeight: 600 }}>{task.account_name || task.company || '—'}</td>
                  <td style={{ fontWeight: 500 }}>
                    {task.title}
                    {task.source_meeting && (
                      <button type="button" title={`Toplantıdan: ${task.source_meeting.title || ''} — kayıtta bu ana git`}
                        onClick={(e) => { e.stopPropagation(); openPlayer(task.source_meeting.meeting_id, task.source_meeting.ts_seconds); }}
                        style={{ marginLeft: 6, background: '#D4FF3A22', color: '#D4FF3A', border: 'none', borderRadius: 6, padding: '1px 6px', fontSize: 11, cursor: 'pointer' }}>
                        <Icon name="play" size={10} /> toplantı
                      </button>
                    )}
                  </td>
                  <td className="text-muted" style={{ maxWidth: 280, whiteSpace: 'normal' }}>{task.last_action || '—'}</td>
                  <td>{Array.isArray(task.assignees) && task.assignees.length ? task.assignees.map(a => a.name).join(', ') : (task.assigned_name || '—')}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <select
                      className="kanban__card-select"
                      value={task.status}
                      onChange={(e) => updateTaskStatus(task.id, e.target.value)}
                      style={{ background: STATUS_MAP[task.status]?.color + '22', color: STATUS_MAP[task.status]?.color, border: 'none', borderRadius: 9999, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}
                    >
                      {STATUS_OPTIONS.map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </td>
                  <td>
                    {task.approval_required
                      ? <span className="badge" style={{ background: '#F2B84B22', color: '#F2B84B' }}>Onay Gerekli</span>
                      : <span className="text-muted">—</span>}
                  </td>
                  <td className="text-muted">{formatUpdated(task.updated_at)}</td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 4, background: PRIORITY_MAP[task.priority]?.color, display: 'inline-block' }} />
                      {PRIORITY_MAP[task.priority]?.label || task.priority}
                    </span>
                  </td>
                  <td>
                    <span className="badge" style={{ background: '#9A9A9222', color: '#9A9A92' }}>
                      {task.related_to === 'lead' ? 'Lead' : task.related_to === 'account' ? 'Hesap' : 'Internal'}
                    </span>
                  </td>
                  <td>{formatDate(task.due_date)}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="10" style={{ textAlign: 'center', padding: 32 }} className="text-muted">Görev bulunamadı</td></tr>
              )}
            </tbody>
          </table>
          </div>
        )}
      </div>
      )}

          </div>
        </div>
      )}

      {player && <MeetingVideoModal meeting={player.meeting} startAt={player.startAt} onClose={() => setPlayer(null)}
        onChanged={() => { fetchTasks(); setSuggestionRefresh(n => n + 1); }} />}

      {/* Task Form / Detail */}
      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal__title" style={{ marginBottom: 8 }}>{editingId ? 'Görev Detayı' : fromItem ? 'Toplantıdan Görev' : 'Yeni Görev'}</h2>
            {fromItem && (
              <p className="text-muted" style={{ marginTop: 0, marginBottom: 16, fontSize: 12 }}>
                Kaynak: {fromItem.meeting_title} — kaydedince bu madde "Göreve çevrilen"e taşınır.
              </p>
            )}
            {editingTask && (
              <p className="text-muted" style={{ marginTop: 0, marginBottom: 20, fontSize: 12 }}>
                Oluşturulma: {formatUpdated(editingTask.created_at)}
                {editingTask.created_by_name ? ` · ${editingTask.created_by_name}` : ''}
                {' '}· Son güncelleme: {formatUpdated(editingTask.updated_at)}
              </p>
            )}
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Marka</label>
                  <select className="form-input" value={form.account_id || ''}
                    onChange={(e) => setForm({ ...form, account_id: e.target.value, company: e.target.value ? '' : form.company })}>
                    <option value="">— Marka yok (iç iş) —</option>
                    {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                  {!form.account_id && form.company && (
                    <div className="text-muted" style={{ fontSize: 11.5, marginTop: 4 }}>Eski firma bilgisi: {form.company} — listeden markayı seçebilirsiniz.</div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Başlık *</label>
                  <input className="form-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Kategori</label>
                <select className="form-input" value={form.category || ''} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  <option value="">Kategorisiz</option>
                  {CATEGORY_OPTIONS.map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Yapılan Son İşlem</label>
                <textarea className="form-input" value={form.last_action} onChange={(e) => setForm({ ...form, last_action: e.target.value })} rows={2} style={{ resize: 'vertical' }} />
              </div>
              <div className="form-group">
                <label className="form-label">Not (Açıklama)</label>
                <textarea className="form-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} style={{ resize: 'vertical' }} />
              </div>
              <div className="form-group">
                <label className="form-label">Sonraki Adım</label>
                <input className="form-input" value={form.next_step} onChange={(e) => setForm({ ...form, next_step: e.target.value })} placeholder="Bir sonraki aksiyon" />
              </div>
              <div className="form-group" style={{ position: 'relative' }}>
                <label className="form-label">Atanan Kişiler <span className="text-muted" style={{ fontWeight: 400 }}>(birden fazla seçilebilir)</span></label>
                <div
                  className="form-input"
                  style={{ cursor: 'pointer', minHeight: 40, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}
                  onClick={() => setAssigneeOpen(o => !o)}
                >
                  {(form.assignee_ids || []).length === 0
                    ? <span className="text-muted">Seçiniz…</span>
                    : users.filter(u => form.assignee_ids.includes(u.id)).map(u => (
                        <span key={u.id} style={{ background: '#D4FF3A22', color: '#D4FF3A', borderRadius: 6, padding: '2px 8px', fontSize: 13 }}>{u.name}</span>
                      ))}
                  <span style={{ marginLeft: 'auto', opacity: 0.6 }}>▾</span>
                </div>
                {assigneeOpen && (
                  <>
                    <div onClick={() => setAssigneeOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 9 }} />
                    <div style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, marginTop: 4, background: '#17191C', border: '1px solid #2A2D32', borderRadius: 8, maxHeight: 240, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,.45)' }}>
                      {users.map(u => {
                        const checked = (form.assignee_ids || []).includes(u.id);
                        return (
                          <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 14 }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => setForm(f => ({
                                ...f,
                                assignee_ids: e.target.checked
                                  ? [...(f.assignee_ids || []), u.id]
                                  : (f.assignee_ids || []).filter(id => id !== u.id),
                              }))}
                            />
                            {u.name}
                          </label>
                        );
                      })}
                      {users.length === 0 && <div className="text-muted" style={{ padding: '8px 12px' }}>Kullanıcı yok</div>}
                    </div>
                  </>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Bitiş Tarihi</label>
                <input className="form-input" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Durum</label>
                  <select className="form-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    {STATUS_OPTIONS.map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Öncelik</label>
                  <select className="form-input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                    <option value="low">Düşük</option>
                    <option value="medium">Orta</option>
                    <option value="high">Yüksek</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">İlgili</label>
                  <select className="form-input" value={form.related_to} onChange={(e) => setForm({ ...form, related_to: e.target.value })}>
                    <option value="internal">Internal</option>
                    <option value="lead">Lead</option>
                    <option value="account">Hesap</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.approval_required} onChange={(e) => setForm({ ...form, approval_required: e.target.checked })} />
                  Onay Gerekli mi?
                </label>
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <button type="submit" className="btn btn--primary" style={{ flex: 1 }}>{editingId ? 'Güncelle' : 'Kaydet'}</button>
                {editingId && (user?.role === 'admin' || editingTask?.created_by === user?.id) && (
                  <button type="button" className="btn" onClick={handleDelete} style={{ background: '#FF6B5B', color: '#0E0F11' }}>Sil</button>
                )}
                <button type="button" className="btn btn--secondary" style={{ flex: 1 }} onClick={closeForm}>İptal</button>
              </div>
            </form>
            {editingId && (
              <TaskComments taskId={editingId} users={users}
                onCountChange={(n) => setTasks(ts => ts.map(t => (t.id === editingId ? { ...t, comment_count: n } : t)))} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
